import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import {
  QuizAccuracy,
  QuizChartDataSlice,
  QuizConfidence,
  QuizInnerChartDataSlice,
  QuizProgressItem,
  QuizResponseItemProps,
} from '../../types/quiz';
import {
  groupQuizItems,
  outerSlices,
  quizTimes,
} from '../../lib/frontend/quiz/scoreboard';
import { DataInnerSlice, DataSlice } from './QuizSelectionChart';
import { useQuizContext } from '../../context/QuizContext';

export enum Filter {
  All,
  New,
  Incorrect,
  NotAnsweredToday,
  IncorrectHigh,
  IncorrectMedium,
  IncorrectLow,
  CorrectHigh,
  CorrectMedium,
  CorrectLow,
}
export enum PieChartDataFilter {
  Null,
  New,
  NotAnsweredToday,
  IncorrectHigh,
  IncorrectMedium,
  IncorrectLow,
  CorrectHigh,
  CorrectMedium,
  CorrectLow,
}
interface OuterPieChartData {
  name: PieChartDataFilter;
  value: number;
  color: string;
}

const incorrectSliceIds = [
  'incorrect-with-high-confidence',
  'incorrect-with-medium-confidence',
  'incorrect-with-low-confidence',
];

/**
 * React Hook which tells the piechart what data to show and how to sum up the count which will be displayed based on filter selected.
 * @returns {Object} {} - Values to be consumed by QuizProgress component.
 */
const useQuizProgress = (quizItems: QuizProgressItem[] = []) => {
  const { red, gray10, lavender, green } = useColors();
  const colors = useColors();
  const intl = useIntl();

  const totalItems = quizItems.length;
  const correctItems = quizItems.filter(
    (i) => i.accuracy === QuizAccuracy.CORRECT && i.confidence !== undefined,
  );
  const incorrectItems = quizItems.filter(
    (i) => i.accuracy === QuizAccuracy.INCORRECT && i.confidence !== undefined,
  );

  const [activeIndices, setActiveIndices] = useState<number[]>([]);
  const [activeInnerIndices, setActiveInnerIndices] = useState<number[]>([]);
  const [currentFilter, setCurrentFilter] = useState<Filter>();

  const { isComplete } = useQuizContext();

  const outerPieChartData: QuizChartDataSlice[] = useMemo(
    () =>
      outerSlices(intl)
        .map((slice, i) => {
          const items = quizItems.filter(slice.filter);
          if (items.length) {
            return {
              name: i,
              value: items.length,
              color: slice.color(colors),
              text: slice.text,
              items,
              id: slice.id,
            };
          }
        })
        .filter(Boolean)
        .map((e, i) => ({ ...e, index: i })) as QuizChartDataSlice[],
    [intl, quizItems, colors],
  );

  const innerPieChartData = outerPieChartData
    .reduce((arr, outerData) => {
      // Group each slice into their respective group times
      const timedItems = groupQuizItems(intl, outerData.items);

      // Flatten timed items into array that matches up with the parent outer ring
      const timedItemsArray = Object.entries(timedItems).reduce(
        (a: QuizInnerChartDataSlice[], [id, values], i) => {
          const timeDefinition = quizTimes(intl).find((t) => t.id === id);
          return [
            ...a,
            {
              name: i,
              value: values.length,
              color: timeDefinition?.color(colors),
              text: timeDefinition?.text,
              items: values,
              id,
              parentName: outerData.index,
            },
          ] as QuizInnerChartDataSlice[];
        },
        [] as QuizInnerChartDataSlice[],
      );

      return [...arr, ...timedItemsArray] as QuizInnerChartDataSlice[];
    }, [] as QuizInnerChartDataSlice[])
    .map((e, i) => ({ ...e, index: i }));

  // Check an inner item (time)
  const handleCheckInner = (indices: number[]) => {
    const rest = activeInnerIndices.filter((i) => !indices.includes(i));
    if (indices.every((index) => activeInnerIndices.includes(index))) {
      rest.length && setActiveInnerIndices(rest);
    } else {
      setActiveInnerIndices([...indices, ...rest]);
    }
  };

  const isFirstSliceNew =
    outerPieChartData.length !== 1 || outerPieChartData[0]?.id !== 'new';

  // Click on an outer slice
  const handleSliceClick = (index: number) => {
    const newActiveIndices = [...activeIndices];

    const prevIndex = activeIndices.findIndex((i) => i === index);
    if (prevIndex > -1) {
      newActiveIndices.splice(prevIndex, 1);
    } else {
      newActiveIndices.push(index);
    }

    const newInnerActiveIndices = innerPieChartData
      .filter((s) => newActiveIndices.includes(s.parentName))
      .map((s) => s.index);

    setActiveInnerIndices(newInnerActiveIndices);
    setActiveIndices(newActiveIndices);
  };

  const handleChartSliceClick = (data: DataSlice, index: number) => {
    if (isFirstSliceNew) {
      handleSliceClick(index);
    }
  };

  // Click on an inner slice
  const handleInnerSliceClick = (index: number, parentIndex: number) => {
    const newActiveIndices: number[] = [...activeInnerIndices];
    const prevIndex = activeInnerIndices.findIndex((i) => i === index);
    if (prevIndex > -1) {
      newActiveIndices.splice(prevIndex, 1);

      if (parentIndex) {
        const isParentSelected = innerPieChartData
          .filter((i) => i.parentName === parentIndex)
          .some((i) => newActiveIndices.includes(i.index));
        !isParentSelected && handleSliceClick(parentIndex);
      }
    } else {
      newActiveIndices.push(index);
      parentIndex &&
        !activeIndices.includes(parentIndex) &&
        handleSliceClick(parentIndex);
    }
    setActiveInnerIndices(newActiveIndices);
  };

  const handleInnerChartSliceClick = (data: DataInnerSlice, index: number) => {
    handleInnerSliceClick(index, data.parentName);
  };

  const handleCheck = (index: number) => {
    handleSliceClick(index);
  };

  const handleFilterClick = (filter: Filter) => {
    let newActiveIndices: number[] = [];

    const outerSlicesTranslated = outerSlices(intl);
    switch (filter) {
      case Filter.New:
        newActiveIndices.push(
          outerSlicesTranslated.findIndex((s) => s.filterType === Filter.New),
        );
        if (isComplete) {
          newActiveIndices = [];
        }
        break;
      case Filter.All:
        newActiveIndices.push(...outerSlicesTranslated.map((slice, i) => i));
        break;
      case Filter.Incorrect: {
        newActiveIndices.push(
          ...outerPieChartData
            .filter((s) => incorrectSliceIds.includes(s.id))
            .map((s) => s.index),
        );
        break;
      }
      case Filter.NotAnsweredToday:
        newActiveIndices.push(
          outerSlicesTranslated.findIndex((s) => s.filterType === Filter.New),
        );
        if (isComplete) {
          newActiveIndices = [];
        }
        break;
    }

    const newActiveInnerIndices = innerPieChartData
      .filter((s) => newActiveIndices.includes(s.parentName))
      .map((s) => s.index);

    setCurrentFilter(filter);
    setActiveIndices(newActiveIndices);
    setActiveInnerIndices(newActiveInnerIndices);
  };

  const getGroupedAnswersInfo = (
    answersArray: QuizProgressItem[],
    accuracy: QuizAccuracy,
    confidence: QuizConfidence,
  ): QuizResponseItemProps[] =>
    answersArray.reduce(
      (acc: QuizResponseItemProps[], currentItem: QuizProgressItem) => {
        if (
          currentItem.accuracy === accuracy &&
          currentItem.confidence === confidence
        ) {
          acc.push(currentItem);
        }
        return acc;
      },
      [],
    );

  const incorrectHigh = getGroupedAnswersInfo(
    incorrectItems,
    QuizAccuracy.INCORRECT,
    QuizConfidence.HIGH,
  );
  const incorrectMedium = getGroupedAnswersInfo(
    incorrectItems,
    QuizAccuracy.INCORRECT,
    QuizConfidence.MEDIUM,
  );
  const incorrectLow = getGroupedAnswersInfo(
    incorrectItems,
    QuizAccuracy.INCORRECT,
    QuizConfidence.LOW,
  );
  const correctHigh = getGroupedAnswersInfo(
    correctItems,
    QuizAccuracy.CORRECT,
    QuizConfidence.HIGH,
  );
  const correctMedium = getGroupedAnswersInfo(
    correctItems,
    QuizAccuracy.CORRECT,
    QuizConfidence.MEDIUM,
  );
  const correctLow = getGroupedAnswersInfo(
    correctItems,
    QuizAccuracy.CORRECT,
    QuizConfidence.LOW,
  );

  const numberOfCorrectItems: number =
    correctHigh?.length + correctMedium?.length + correctLow?.length || 0;
  const numberOfIncorrectItems: number =
    incorrectHigh?.length + incorrectMedium?.length + incorrectLow?.length || 0;
  const numberOfNewItems: number =
    totalItems - numberOfCorrectItems - numberOfIncorrectItems;

  const numberOfItemsNotAnsweredToday: number =
    quizItems.filter((i) => !i.answered).length || 0;

  const defaultOuterPieChartData: OuterPieChartData[] = [
    {
      name: PieChartDataFilter.New,
      value: numberOfNewItems,
      color: gray10,
    },
    {
      name: PieChartDataFilter.IncorrectHigh,
      value: incorrectHigh?.length,
      color: '#E78D8D', // pink
    },
    {
      name: PieChartDataFilter.IncorrectMedium,
      value: incorrectMedium?.length,
      color: red,
    },
    {
      name: PieChartDataFilter.IncorrectLow,
      value: incorrectLow?.length,
      color: '#8B1D1D', // dark red
    },
    {
      name: PieChartDataFilter.CorrectHigh,
      value: correctHigh?.length,
      color: '#6DD56D', // light green
    },
    {
      name: PieChartDataFilter.CorrectMedium,
      value: correctMedium?.length,
      color: green,
    },
    {
      name: PieChartDataFilter.CorrectLow,
      value: correctLow?.length,
      color: '#1A5B1B', // dark green
    },
    {
      name: PieChartDataFilter.NotAnsweredToday,
      value: numberOfItemsNotAnsweredToday,
      color: lavender,
    },
  ];

  const answeredQuestions: number =
    quizItems.filter((i) => i.answered)?.length || 0;
  const unansweredQuestions: number = totalItems - answeredQuestions;

  let overLayText = intl.formatMessage(
    {
      id: 'quiz.progress.count_answered',
      defaultMessage: '{unansweredQuestions} unanswered',
    },
    { unansweredQuestions },
  );

  if (currentFilter === Filter.New) {
    overLayText = intl.formatMessage(
      {
        id: 'quiz.progress.count_unanswered',
        defaultMessage: `{count} unanswered`,
      },
      { count: unansweredQuestions },
    );
  } else if (currentFilter === Filter.All) {
    overLayText = intl.formatMessage(
      {
        id: 'quiz.progress.count_answered_unanswered',
        defaultMessage: `{answeredQuestions} answered/{unansweredQuestions} unanswered`,
      },
      { answeredQuestions, unansweredQuestions },
    );
  }

  const isInnerPieVisisble = useMemo(
    () =>
      innerPieChartData
        .filter((i) => i.id !== 'NA')
        .some((i) => activeInnerIndices.includes(i.index)),
    [activeInnerIndices, innerPieChartData],
  );

  // Initialize filters if none currently clicked
  useEffect(() => {
    if (quizItems.length && typeof currentFilter === 'undefined') {
      handleFilterClick(Filter.All);
    }
  }, [quizItems, currentFilter]);

  return {
    outerPieChartData,
    innerPieChartData,
    activeIndices,
    activeInnerIndices,
    currentFilter,
    overLayText,
    defaultOuterPieChartData,
    isFirstSliceNew,
    setCurrentFilter,
    setActiveIndices,
    setActiveInnerIndices,
    handleCheckInner,
    handleChartSliceClick,
    handleInnerChartSliceClick,
    handleCheck,
    handleFilterClick,
    isInnerPieVisisble,
    totalItems,
    correctItems,
    incorrectItems,
    incorrectHigh,
    incorrectLow,
    incorrectMedium,
    correctHigh,
    correctMedium,
    correctLow,
    numberOfNewItems,
  };
};

export default useQuizProgress;
